import axiosClient from "utils/axios";
import { ApiList, ApiResponse } from "types/api.types";
import type {
  CreateLeaveApplicationPayload,
  LeaveApplication,
  UpdateLeaveApplicationPayload,
} from "../types/leave-application.types";

export const getLeaveApplications = async ({
  page = 1,
  limit = 10,
  status,
  employeeId,
  leaveTypeId,
}: {
  page?: number;
  limit?: number;
  search?: string;
  status?: string;
  employeeId?: number;
  leaveTypeId?: number;
} = {}) => {
  const response = await axiosClient.get<ApiResponse<ApiList<LeaveApplication[]>>>(
    "/leave-applications",
    {
      params: {
        page,
        limit,
        status: status || undefined,
        employeeId:
          employeeId != null && Number.isFinite(employeeId) && employeeId > 0
            ? employeeId
            : undefined,
        leaveTypeId:
          leaveTypeId != null && Number.isFinite(leaveTypeId) && leaveTypeId > 0
            ? leaveTypeId
            : undefined,
      },
    },
  );
  return response.data;
};

export const getLeaveApplication = async (id: number) => {
  const response = await axiosClient.get<ApiResponse<LeaveApplication>>(
    `/leave-applications/${id}`,
  );
  return response.data;
};

export const createLeaveApplication = async (
  payload: CreateLeaveApplicationPayload,
) => {
  const response = await axiosClient.post<ApiResponse<LeaveApplication>>(
    "/leave-applications",
    payload,
  );
  return response.data;
};

export const updateLeaveApplication = async (
  id: number,
  payload: UpdateLeaveApplicationPayload,
) => {
  const response = await axiosClient.put<ApiResponse<LeaveApplication>>(
    `/leave-applications/${id}`,
    payload,
  );
  return response.data;
};

export const approveLeaveApplication = async (id: number) => {
  const response = await axiosClient.post<ApiResponse<LeaveApplication>>(
    `/leave-applications/${id}/approve`,
    {},
  );
  return response.data;
};

export const rejectLeaveApplication = async (
  id: number,
  body: { reason?: string | null } = {},
) => {
  const response = await axiosClient.post<ApiResponse<LeaveApplication>>(
    `/leave-applications/${id}/reject`,
    body,
  );
  return response.data;
};

/** APPROVED applications only (`leave_application.cancel`). Deletes linked LEAVE attendance records. */
export const cancelLeaveApplication = async (
  id: number,
  body: { reason?: string | null } = {},
) => {
  const response = await axiosClient.post<ApiResponse<LeaveApplication>>(
    `/leave-applications/${id}/cancel`,
    body,
  );
  return response.data;
};

/** PENDING applications only (`leave_application.delete`). */
export const deleteLeaveApplication = async (id: number) => {
  const response = await axiosClient.delete<ApiResponse<unknown>>(
    `/leave-applications/${id}`,
  );
  return response.data;
};

export interface LeaveSummaryMonth {
  month: number;
  year: number;
  presentDays: number;
  threshold: number;
  metThreshold: boolean;
  paidLeaveQuota: number;
  paidLeavesTaken: number;
  paidLeavesRemaining: number;
  unpaidLeavesTaken: number;
  nextLeaveIsLop: boolean;
}

export const getLeaveApplicationSummary = async ({
  fromDate,
  toDate,
  employeeId,
}: {
  fromDate: string;
  toDate: string;
  employeeId?: number;
}) => {
  const response = await axiosClient.get<ApiResponse<LeaveSummaryMonth[]>>(
    "/leave-applications/summary",
    {
      params: {
        fromDate,
        toDate,
        employeeId: employeeId != null && employeeId > 0 ? employeeId : undefined,
      },
    },
  );
  return response.data;
};
